import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Save, ArrowLeft, FileText, Search, Check, UserPlus } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { formatCurrencyINR } from "../utils/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLANK_ITEM = { name: "", description: "", hsn_code: "", quantity: 1, unit_price: 0, gst_percent: 18, gst_not_applicable: false };

export default function QuotationBuilderPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [saving, setSaving] = useState(false);

  // Customer master state
  const [customers, setCustomers] = useState([]);
  const [showCustDropdown, setShowCustDropdown] = useState(false);
  const [saveToMaster, setSaveToMaster] = useState(true);
  const dropdownRef = useRef(null);

  const [form, setForm] = useState({
    company_id: "",
    customer_id: "",
    customer_name: "",
    customer_email: "",
    customer_gst: "",
    customer_phone: "",
    customer_address: "",
    quotation_date: new Date().toISOString().split("T")[0],
    valid_until: "",
    notes: "",
    terms: "",
  });
  const [items, setItems] = useState([{ ...BLANK_ITEM }]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/companies`, { withCredentials: true }),
      axios.get(`${API}/customers/`, { withCredentials: true }),
    ]).then(([compRes, custRes]) => {
      const list = compRes.data.companies || [];
      setCompanies(list);
      const custList = custRes.data.customers || [];
      setCustomers(custList);
      const initial = { company_id: list[0]?.company_id || "" };
      // Pre-select customer from URL if provided
      const presetId = params.get("customer_id");
      if (presetId) {
        const c = custList.find(x => x.customer_id === presetId);
        if (c) {
          initial.customer_id = c.customer_id;
          initial.customer_name = c.name;
          initial.customer_gst = c.gstin || "";
          initial.customer_email = c.email || "";
          initial.customer_phone = c.phone || "";
          initial.customer_address = c.address || "";
        }
      }
      setForm(p => ({ ...p, ...initial }));
    }).catch(() => {});
  }, []); // eslint-disable-line

  // Close customer dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowCustDropdown(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const company = companies.find(c => c.company_id === form.company_id);
  const isGstRegistered = company
    ? (company.gst_registered !== undefined ? !!company.gst_registered : !!(company.gst_number || "").trim())
    : true;

  const filteredCustomers = useMemo(() => {
    const search = (form.customer_name || "").toLowerCase().trim();
    if (!search) return customers.slice(0, 12);
    return customers.filter(c =>
      c.name.toLowerCase().includes(search) ||
      (c.gstin || "").toLowerCase().includes(search) ||
      (c.email || "").toLowerCase().includes(search)
    ).slice(0, 12);
  }, [customers, form.customer_name]);

  const totals = useMemo(() => {
    let sub = 0, tax = 0, nonGst = 0;
    items.forEach(it => {
      const q = Number(it.quantity) || 0;
      const r = Number(it.unit_price) || 0;
      const base = q * r;
      if (it.gst_not_applicable || !isGstRegistered) {
        if (it.gst_not_applicable) nonGst += base;
        else sub += base;
      } else {
        sub += base;
        tax += base * (Number(it.gst_percent) || 0) / 100;
      }
    });
    return {
      subtotal: Math.round(sub * 100) / 100,
      tax: Math.round(tax * 100) / 100,
      nonGst: Math.round(nonGst * 100) / 100,
      grand: Math.round((sub + tax + nonGst) * 100) / 100,
    };
  }, [items, isGstRegistered]);

  const updateItem = (idx, key, value) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, [key]: value } : it));
  const addItem = () => setItems(prev => [...prev, { ...BLANK_ITEM }]);
  const removeItem = (idx) => setItems(prev => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));

  const selectCustomer = (c) => {
    setForm(p => ({
      ...p,
      customer_id: c.customer_id,
      customer_name: c.name,
      customer_gst: c.gstin || "",
      customer_email: c.email || "",
      customer_phone: c.phone || "",
      customer_address: c.address || "",
    }));
    setShowCustDropdown(false);
    setSaveToMaster(false);  // already in master
  };

  const onCustomerNameChange = (value) => {
    setForm(p => ({ ...p, customer_name: value, customer_id: "" }));
    setShowCustDropdown(true);
    setSaveToMaster(true);
  };

  const handleSave = async () => {
    if (!form.company_id) { toast.error("Select a company"); return; }
    if (!form.customer_name.trim()) { toast.error("Customer name is required"); return; }
    if (!items.some(i => i.name.trim())) { toast.error("Add at least one line item"); return; }
    setSaving(true);
    try {
      let customer_id = form.customer_id;

      // Save new customer to master if needed
      if (!customer_id && saveToMaster) {
        try {
          const res = await axios.post(`${API}/customers/`, {
            name: form.customer_name.trim(),
            gstin: form.customer_gst || null,
            email: form.customer_email || null,
            phone: form.customer_phone || null,
            address: form.customer_address || null,
            company_id: form.company_id,
          }, { withCredentials: true });
          customer_id = res.data.customer_id;
          toast.success(`"${res.data.name}" added to your customer master`);
        } catch (custErr) {
          // Non-fatal — still create the quotation
          console.error("Customer save failed:", custErr);
        }
      }

      const payload = {
        ...form,
        customer_id: customer_id || null,
        items: items.filter(i => i.name.trim()).map(i => ({
          name: i.name,
          description: i.description || "",
          hsn_code: i.hsn_code || "",
          quantity: Number(i.quantity) || 0,
          unit_price: Number(i.unit_price) || 0,
          gst_percent: Number(i.gst_percent) || 0,
          gst_not_applicable: !!i.gst_not_applicable,
        })),
      };
      const res = await axios.post(`${API}/quotations/create`, payload, { withCredentials: true });
      toast.success("Quotation created");
      navigate(`/quotations/${res.data.quotation_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (companies.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <FileText className="w-10 h-10 text-blue-600 mx-auto mb-3" />
          <p className="text-slate-700 mb-3">Please set up a company first.</p>
          <Button onClick={() => navigate("/settings")} className="bg-blue-600 text-white">Open Settings</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4" data-testid="quotation-builder-page">
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate("/quotations")} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Quotations
        </button>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>New Quotation</h1>
        <p className="text-sm text-slate-500 mb-5">Send a proposal, get approval online, convert to invoice in one click.</p>

        {/* Company + dates */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 mb-4 grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">From Company</label>
            <select
              value={form.company_id}
              onChange={e => setForm(p => ({ ...p, company_id: e.target.value }))}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              data-testid="q-company-select"
            >
              {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.company_name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Quotation Date</label>
              <Input type="date" value={form.quotation_date} onChange={e => setForm(p => ({ ...p, quotation_date: e.target.value }))} data-testid="q-date" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Valid Until</label>
              <Input type="date" value={form.valid_until} onChange={e => setForm(p => ({ ...p, valid_until: e.target.value }))} data-testid="q-valid-until" />
            </div>
          </div>
        </div>

        {/* Customer */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Client Details</p>
            {form.customer_id && (
              <span className="text-[11px] inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full font-semibold" data-testid="q-customer-from-master">
                <Check className="w-3 h-3" /> From Master
              </span>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {/* Searchable name with dropdown */}
            <div className="relative" ref={dropdownRef}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              <Input
                placeholder="Client name * (search master or type new)"
                value={form.customer_name}
                onChange={e => onCustomerNameChange(e.target.value)}
                onFocus={() => setShowCustDropdown(true)}
                className="pl-9"
                data-testid="q-customer-name"
              />
              {showCustDropdown && filteredCustomers.length > 0 && (
                <div className="absolute z-20 left-0 right-0 mt-1 bg-white rounded-xl border border-slate-200 shadow-lg max-h-60 overflow-y-auto" data-testid="q-customer-dropdown">
                  {filteredCustomers.map(c => (
                    <button
                      key={c.customer_id}
                      onClick={() => selectCustomer(c)}
                      type="button"
                      className="flex items-center gap-3 w-full px-3 py-2 hover:bg-slate-50 text-left"
                      data-testid={`q-cust-option-${c.customer_id}`}
                    >
                      <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center text-[11px] font-bold">{c.name.charAt(0)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{c.name}</p>
                        <p className="text-[11px] text-slate-500 truncate">{c.gstin || c.email || c.phone || "—"}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Input placeholder="Client email" type="email" value={form.customer_email} onChange={e => setForm(p => ({ ...p, customer_email: e.target.value, customer_id: "" }))} data-testid="q-customer-email" />
            <Input placeholder="Client GSTIN (optional)" value={form.customer_gst} onChange={e => setForm(p => ({ ...p, customer_gst: e.target.value.toUpperCase(), customer_id: "" }))} className="uppercase font-mono" maxLength={15} data-testid="q-customer-gst" />
            <Input placeholder="Phone" value={form.customer_phone} onChange={e => setForm(p => ({ ...p, customer_phone: e.target.value, customer_id: "" }))} data-testid="q-customer-phone" />
            <Input className="sm:col-span-2" placeholder="Billing address" value={form.customer_address} onChange={e => setForm(p => ({ ...p, customer_address: e.target.value, customer_id: "" }))} data-testid="q-customer-address" />
          </div>

          {/* Save-to-master toggle (only when customer is new) */}
          {!form.customer_id && form.customer_name.trim() && (
            <label className="flex items-center gap-2 mt-3 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100 cursor-pointer" data-testid="q-save-to-master-toggle">
              <input
                type="checkbox"
                checked={saveToMaster}
                onChange={e => setSaveToMaster(e.target.checked)}
                className="w-4 h-4 rounded text-blue-600"
              />
              <UserPlus className="w-4 h-4 text-blue-600" />
              <span className="text-xs text-blue-900">
                <strong>Save this client to your customer master</strong> — auto-fills next time and tracks all their invoices/quotations together.
              </span>
            </label>
          )}
        </div>

        {/* Items */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 mb-4" data-testid="q-items">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Line Items</p>
            <button onClick={addItem} className="text-xs font-semibold text-blue-600 hover:text-blue-800 inline-flex items-center gap-1" data-testid="q-add-item"><Plus className="w-3 h-3" /> Add row</button>
          </div>
          <div className="space-y-3">
            {items.map((it, i) => (
              <div key={i} className={`grid grid-cols-2 ${isGstRegistered ? "sm:grid-cols-6" : "sm:grid-cols-5"} gap-2 items-end border-b border-slate-50 pb-3 last:border-0`} data-testid={`q-item-${i}`}>
                <div className="col-span-2 sm:col-span-2">
                  <label className="block text-xs text-slate-500 mb-1">Item</label>
                  <Input value={it.name} onChange={e => updateItem(i, "name", e.target.value)} placeholder="Name" className="text-sm" />
                  <Input value={it.description} onChange={e => updateItem(i, "description", e.target.value)} placeholder="Description (optional)" className="text-xs mt-1 text-slate-500" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Qty</label>
                  <Input type="number" value={it.quantity} onChange={e => updateItem(i, "quantity", parseFloat(e.target.value) || 0)} className="text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Rate</label>
                  <Input type="number" value={it.unit_price} onChange={e => updateItem(i, "unit_price", parseFloat(e.target.value) || 0)} className="text-sm" />
                </div>
                {isGstRegistered && (
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">GST %</label>
                    <Input type="number" value={it.gst_percent} onChange={e => updateItem(i, "gst_percent", parseFloat(e.target.value) || 0)} className="text-sm" />
                  </div>
                )}
                <div className="flex items-end gap-1">
                  <span className="text-sm font-semibold text-slate-900 flex-1 text-right">
                    {formatCurrencyINR((Number(it.quantity) || 0) * (Number(it.unit_price) || 0) * (isGstRegistered && !it.gst_not_applicable ? 1 + (Number(it.gst_percent) || 0) / 100 : 1))}
                  </span>
                  <button onClick={() => removeItem(i)} className="p-1.5 text-slate-300 hover:text-red-500 transition-colors" data-testid={`q-delete-item-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="border-t border-slate-100 pt-4 mt-4 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">{isGstRegistered ? "Subtotal (Excl. Tax)" : "Subtotal"}</span><span>{formatCurrencyINR(totals.subtotal)}</span></div>
            {isGstRegistered && <div className="flex justify-between"><span className="text-slate-500">GST</span><span>{formatCurrencyINR(totals.tax)}</span></div>}
            {totals.nonGst > 0 && <div className="flex justify-between"><span className="text-amber-700">Non-GST</span><span>{formatCurrencyINR(totals.nonGst)}</span></div>}
            <div className="flex justify-between pt-1 border-t border-dashed border-slate-100 text-base font-bold text-slate-900" data-testid="q-grand-total">
              <span>Grand Total</span><span>{formatCurrencyINR(totals.grand)}</span>
            </div>
          </div>
        </div>

        {/* Notes / Terms */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 mb-4 grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Notes to client</label>
            <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={3} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="q-notes" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Terms &amp; Conditions</label>
            <textarea value={form.terms} onChange={e => setForm(p => ({ ...p, terms: e.target.value }))} rows={3} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="q-terms" />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => navigate("/quotations")}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="q-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save as Draft
          </Button>
        </div>
      </div>
    </div>
  );
}
